import { Fragment, useEffect, useState } from 'react';
import styles from './TableColumnGrouped.module.scss';

export interface TableColumnGroupedPropsColumnConfiguration<T> {
	header: TableColumnGroupedPropsColumn<T>;
	groups: TableColumnGroupedPropsColumnGroup<T>[];
}

export interface TableColumnGroupedPropsColumnGroup<T> {
	title: string;
	columns: TableColumnGroupedPropsColumn<T>[];
	defaults: {
		isExpanded: boolean;
	}
}

export interface TableColumnGroupedPropsColumn<T> {
	title: string | JSX.Element;
	renderCell: (item: T) => JSX.Element;
	classNames?: {
		dataCell?: string;
	};
	comparer?: (first: T, second: T) => number;
}

export interface TableColumnGroupedProps<T> {
	items: T[];
	columnConfiguration: TableColumnGroupedPropsColumnConfiguration<T>;

	getUniqueId: (item: T) => string;
}

interface SortingSettings<T> {
	direction: SortDirection;
	column: TableColumnGroupedPropsColumn<T>;
}
enum SortDirection {
	ASC = "ASC",
	DESC = "DESC"
}

export const TableColumnGrouped = <T extends object>(props: TableColumnGroupedProps<T>) => {
	const [groupsVisibility, setGroupsVisibility] = useState<boolean[]>(props.columnConfiguration.groups.map(x => x.defaults.isExpanded));
	const [items, setItems] = useState<T[]>([]);
	const [sorting, setSorting] = useState<SortingSettings<T> | null>(null);

	useEffect(() => setSortingItems(), [props.items, sorting]);

	return (
		<table className={styles.table}>
			<tbody>
				<HeaderRow
					key={'header'}
					columnConfiguration={props.columnConfiguration}
					rowsCount={items.length}
					groupsVisibility={groupsVisibility}
					sorting={sorting}
					onGroupClick={groupIndex => {
						var newGroupVisibility = groupsVisibility.map(x => x);
						newGroupVisibility[groupIndex] = !newGroupVisibility[groupIndex];
						setGroupsVisibility(newGroupVisibility);
					}}
					onHeaderClick={column => {
						if (!column.comparer)
							return;

						if (sorting == null || sorting.column !== column) {
							setSorting({ column, direction: SortDirection.ASC });
						}
						else {
							var revertSorting = sorting.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
							setSorting({ column, direction: revertSorting });
						}
					}} />
				{
					items.map(item => (
						<BodyRow
							key={props.getUniqueId(item)}
							item={item}
							columnConfiguration={props.columnConfiguration}
							groupsVisibility={groupsVisibility} />
					))
				}
			</tbody>
		</table>
	);

	function setSortingItems(): void {
		if (sorting == null) {
			setItems(props.items);
			return;
		}

		const comparer = sorting.column.comparer;
		if (!comparer) {
			setItems(props.items);
			return;
		}

		const multiplier = sorting.direction === SortDirection.ASC ? 1 : -1;
		var newItems = [...props.items];
		newItems.sort((x, y) => multiplier * comparer(x, y));
		
		setItems(newItems);
	}
}


interface HeaderRowProps<T> {
	rowsCount: number;
	columnConfiguration: TableColumnGroupedPropsColumnConfiguration<T>;
	groupsVisibility: boolean[];
	onGroupClick: (groupIndex: number) => void;
	onHeaderClick: (column: TableColumnGroupedPropsColumn<T>) => void;
	sorting: SortingSettings<T> | null;
}
const HeaderRow = <T extends object>(props: HeaderRowProps<T>) => {
	function sortingClases(column: TableColumnGroupedPropsColumn<T>) {
		const classes = [];
		if (column.comparer) {
			classes.push(styles.sortable);

			if (props.sorting != null && props.sorting.column === column)
				classes.push(props.sorting.direction === SortDirection.ASC ? styles.sorted_asc : styles.sorted_desc)
		}

		return cn(...classes);
	}

	function renderGroupColumn(group: TableColumnGroupedPropsColumnGroup<T>, groupIndex: number) {
		return (
			<td
				className={cn(styles.header_group_cell, styles.border, groupIndex === 0 ? styles.border_group_firts : styles.border_group_all)}
				rowSpan={props.rowsCount + 1}
				onClick={() => props.onGroupClick(groupIndex)}>
				<span data-state={props.groupsVisibility[groupIndex] ? 'expanded' : 'collapsed'}>
					<i className='fa'></i>
					{group.title}
				</span>
			</td>
		);
	}
	function renderGroupSubColumns(group: TableColumnGroupedPropsColumnGroup<T>, groupIndex: number) {
		if (!props.groupsVisibility[groupIndex])
			return null;

		return group.columns.map((column, columnIndex) => (
			<td
				key={`column-${groupIndex}-${columnIndex}`}
				className={cn(styles.header_data_cell, styles.sticky_top, sortingClases(column))}
				onClick={() => props.onHeaderClick(column)}>
				<span className={cn(styles.border, styles.border_horizontal, styles.view_cell)}>
					{column.title}
					<i className='fa'></i>
				</span>
			</td>
		))
	}

	return (
		<tr>
			<td
				className={cn(styles.header_data_cell, styles.sticky_both, sortingClases(props.columnConfiguration.header))}
				onClick={() => props.onHeaderClick(props.columnConfiguration.header)}>
				<span className={cn(styles.border, styles.border_horizontal, styles.border_left, styles.border_right, styles.view_cell)}>
					{props.columnConfiguration.header.title}
					<i className='fa'></i>
				</span>
			</td>
			{
				props.columnConfiguration.groups.map((group, groupIndex) => (
					<Fragment key={`group-${groupIndex}`}>
						{ renderGroupColumn(group, groupIndex)}
						{ renderGroupSubColumns(group, groupIndex)}
					</Fragment>
				))
			}
		</tr>
	)
}

interface BodyRowProps<T> {
	item: T;
	columnConfiguration: TableColumnGroupedPropsColumnConfiguration<T>;
	groupsVisibility: boolean[];
}
const BodyRow = <T extends object>(props: BodyRowProps<T>) => {
	function renderGroupSubColumns(group: TableColumnGroupedPropsColumnGroup<T>, groupIndex: number) {
		if (!props.groupsVisibility[groupIndex])
			return null;

		return group.columns.map((column, columnIndex) => (
			<td key={`column-${groupIndex}-${columnIndex}`} className={cn(styles.body_data_cell, column.classNames?.dataCell || '')}>
				<span className={styles.view_cell}>
					{column.renderCell(props.item)}
				</span>
			</td>
		));
	}


	return (
		<tr>
			<td className={cn(styles.body_header_cell, styles.sticky_left)}>
				<span className={cn(styles.border, styles.border_left, styles.border_right, styles.view_cell)}>
					{props.columnConfiguration.header.renderCell(props.item)}
				</span>
			</td>
			{
				props.columnConfiguration.groups.map((group, groupIndex) =>
					renderGroupSubColumns(group, groupIndex)
				)
			}
		</tr>
	)
}

function cn(...values: string[]) {
	return values.join(' ');
}